import React from 'react';
import { Button } from 'antd';
import type { CheckboxProps } from 'antd';
import { BorderOutlined, CheckSquareFilled } from '@ant-design/icons';

// Custom appearance types for the checkbox
export type CheckboxAppearance = 'basic' | 'selected' | 'disabled';

interface AntdCustomCheckboxProps extends  CheckboxProps {
  appearance?: CheckboxAppearance;
  text?: string;
  children?: React.ReactNode;
}

const AntdCustomCheckbox: React.FC<AntdCustomCheckboxProps> = ({
  appearance = 'basic',
  text,
  children,
  checked = false,
  disabled = false,
  onChange,
  onClick,
  className = '',
  style,
  ...rest
}) => {
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      const newChecked = !checked;
      onChange?.({ target: { checked: newChecked } } as any);
      onClick?.(event);
    }
  };

  const getCheckboxProps = () => {
    const baseStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      backgroundColor: '#ffffff',
      height: 'auto',
      padding: '12px 8px',
      border: '1px solid transparent',
      ...style
    };

    if (disabled || appearance === 'disabled') {
      return {
        disabled: true,
        style: {
          ...baseStyle,
          color: 'rgba(0, 0, 0, 0.25)',
          cursor: 'not-allowed',
          border: '1px solid rgba(0, 0, 0, 0.06)'
        }
      };
    }

    const selectedStyle = checked
      ? {
          border: '1px solid #52C41A'
        }
      : {
          border: '1px solid rgba(0, 0, 0, 0.15)'
        };

    return {
      style: {
        ...baseStyle,
        color: 'rgba(0, 0, 0, 0.88)',
        ...selectedStyle
      }
    };
  };

  const renderIcon = () => {
    if (checked && !disabled) {
      return (
        <CheckSquareFilled
          style={{
            color: '#52C41A',
            fontSize: '16px',
            width: '16px',
            height: '16px'
          }}
        />
      );
    }

    switch (appearance) {
      case 'disabled':
        return (
          <div
            style={{
              width: '16px',
              height: '16px',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(0, 0, 0, 0.15)',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        );
      case 'selected':
      case 'basic':
      default:
        return (
          <BorderOutlined
            style={{
              color: 'rgba(0, 0, 0, 0.15)',
              fontSize: '16px',
              width: '16px',
              height: '16px'
            }}
          />
        );
    }
  };

  const renderSeparator = () => (
    <div
      style={{
        width: '1px',
        height: '31px',
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        margin: '0 16px'
      }}
    />
  );

  const checkboxProps = getCheckboxProps();
  const displayText = text || children;

  return (
    <Button
      {...checkboxProps}
      onClick={handleClick}
      className={`${className} custom-checkbox ${disabled ? 'disabled' : ''}`}
      type="default"
      disabled={disabled}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          justifyContent: 'flex-start'
        }}>
        {renderIcon()}
        {renderSeparator()}
        <span>{displayText}</span>
      </div>
    </Button>
  );
};

export default AntdCustomCheckbox;
